import { useTranslation } from 'react-i18next';

const INTL_LOCALE: Record<string, string> = { cs: 'cs-CZ', en: 'en-GB' };
const NBSP = ' ';
const UNITS = ['B', 'kB', 'MB', 'GB', 'TB'];

export function formatBytes(n: number, locale: string): string {
  let v = n;
  let i = 0;
  while (v >= 1000 && i < UNITS.length - 1) {
    v /= 1000;
    i++;
  }
  const digits = i === 0 ? 0 : v < 10 ? 2 : 1;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(v) + NBSP + UNITS[i];
}

const RELATIVE_STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86400],
  ['month', 30 * 86400],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
];

export function formatRelative(iso: string, locale: string, now: number = Date.now()): string {
  const diff = (new Date(iso).getTime() - now) / 1000;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, secs] of RELATIVE_STEPS) {
    if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
  }
  return rtf.format(Math.round(diff), 'second');
}

/** Number formats by locale and digits: making one is far dearer than using it, and tables use thousands. */
const numberFormats = new Map<string, Intl.NumberFormat>();
function numberFormat(locale: string, digits: number): Intl.NumberFormat {
  const key = `${locale}/${digits}`;
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale, { maximumFractionDigits: digits });
    numberFormats.set(key, format);
  }
  return format;
}

/** Locale-aware formatting per frontend-design.md §1: per-locale digits, NBSP before a unit. */
export function useFormat() {
  const { i18n } = useTranslation();
  const locale = INTL_LOCALE[i18n.language] ?? 'cs-CZ';
  return {
    locale,
    bytes: (n: number) => formatBytes(n, locale),
    number: (n: number, digits = 0) => numberFormat(locale, digits).format(n),
    date: (iso: string | null | undefined) => (iso ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso)) : '—'),
    dateTime: (iso: string | null | undefined) =>
      iso ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : '—',
    relative: (iso: string) => formatRelative(iso, locale),
  };
}
